<template>
  <Transition name="toast">
    <div
      v-if="toast.show"
      class="fixed top-5 right-5 z-50 min-w-[320px] max-w-[420px] backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 overflow-hidden"
      :class="[
        toast.type === 'success'
          ? 'bg-gradient-to-r from-emerald-500 to-emerald-600'
          : toast.type === 'error'
          ? 'bg-gradient-to-r from-rose-500 to-rose-600'
          : toast.type === 'warning'
          ? 'bg-gradient-to-r from-amber-500 to-amber-600'
          : toast.type === 'info'
          ? 'bg-gradient-to-r from-sky-500 to-sky-600'
          : 'bg-gradient-to-r from-gray-600 to-gray-700',
      ]"
    >
      <!-- Progress Bar -->
      <div class="h-1 bg-white/20">
        <div
          class="h-full bg-white/80 transition-all duration-100 ease-linear rounded-r-full"
          :style="{ width: progressWidth + '%' }"
        />
      </div>

      <div class="px-5 py-4">
        <div class="flex items-start gap-3">
          <!-- Icon Container -->
          <div
            class="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-xl"
            :class="[
              toast.type === 'success'
                ? 'bg-white/20'
                : toast.type === 'error'
                ? 'bg-white/20'
                : toast.type === 'warning'
                ? 'bg-white/20'
                : toast.type === 'info'
                ? 'bg-white/20'
                : 'bg-white/20',
            ]"
          >
            <component :is="iconComponent" class="w-5 h-5" />
          </div>

          <!-- Content -->
          <div class="flex-1 pt-0.5">
            <p class="text-sm font-semibold text-white leading-snug">
              {{ toast.message }}
            </p>
            <p v-if="toast.description" class="text-xs text-white/80 mt-1">
              {{ toast.description }}
            </p>
          </div>

          <!-- Close Button -->
          <button
            @click="closeToast"
            class="shrink-0 w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200 group"
            aria-label="بستن"
          >
            <X
              class="w-4 h-4 text-white/80 group-hover:text-white transition-colors"
            />
          </button>
        </div>
      </div>

      <!-- Action Button (Optional) -->
      <div v-if="toast.action" class="px-5 pb-4 pt-0">
        <button
          @click="toast.action.onClick"
          class="w-full py-2 px-4 bg-white/15 hover:bg-white/25 rounded-xl text-sm font-medium text-white backdrop-blur-sm transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
        >
          {{ toast.action.label }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from "vue";
import { CheckCircle, XCircle, AlertTriangle, Info, X } from "lucide-vue-next";
import { toastState } from "@/composables/useToast";

const toast = toastState;
const progressWidth = ref(100);
let progressInterval = null;
let hideTimeout = null;
let pauseTimeout = null;

// آیکون متناسب با نوع toast
const iconComponent = computed(() => {
  const icons = {
    success: CheckCircle,
    error: XCircle,
    warning: AlertTriangle,
    info: Info,
  };
  return icons[toast.type] || Info;
});

// بستن با انیمیشن
const closeToast = () => {
  if (progressInterval) clearInterval(progressInterval);
  if (hideTimeout) clearTimeout(hideTimeout);
  progressWidth.value = 0;
  setTimeout(() => {
    toast.show = false;
  }, 200);
};

// توقف progress bar هنگام hover
const pauseProgress = () => {
  if (progressInterval) {
    clearInterval(progressInterval);
    progressInterval = null;
  }
  if (hideTimeout) {
    clearTimeout(hideTimeout);
    hideTimeout = null;
  }
};

const resumeProgress = () => {
  if (toast.show && progressWidth.value > 0) {
    const remainingTime =
      (progressWidth.value / 100) * (toast.duration || 3000);
    startProgress(remainingTime);
  }
};

const startProgress = (duration = 3000) => {
  if (progressInterval) clearInterval(progressInterval);
  if (hideTimeout) clearTimeout(hideTimeout);

  progressWidth.value = 100;

  const intervalTime = 30;
  const step = 100 / (duration / intervalTime);

  progressInterval = setInterval(() => {
    if (progressWidth.value > 0) {
      progressWidth.value = Math.max(0, progressWidth.value - step);
    } else {
      clearInterval(progressInterval);
    }
  }, intervalTime);

  hideTimeout = setTimeout(() => {
    toast.show = false;
  }, duration);
};

// واچ کردن تغییرات toast
watch(
  () => toast.show,
  (newVal) => {
    if (newVal) {
      startProgress(toast.duration || 3000);
    } else {
      if (progressInterval) clearInterval(progressInterval);
      if (hideTimeout) clearTimeout(hideTimeout);
      progressWidth.value = 0;
    }
  }
);

onUnmounted(() => {
  if (progressInterval) clearInterval(progressInterval);
  if (hideTimeout) clearTimeout(hideTimeout);
  if (pauseTimeout) clearTimeout(pauseTimeout);
});
</script>

<style scoped>
.toast-enter-active {
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(120px) scale(0.8) rotate(-3deg);
  filter: blur(4px);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(120px) scale(0.8) rotate(5deg);
  filter: blur(4px);
}
</style>